import { ElementType, lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
// import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import DashboardWithoutHeaderLayout from '../layouts/dashboard/withoutHeaderLayout';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        // { path: 'login-unprotected', element: <Login /> },
        // { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password/:token', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'system',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'cases/list', element: <CaseList /> },
        { path: 'cases/excluded', element: <CasesExcluded /> },
        { path: 'cases/archived', element: <CasesArchived /> },
        { path: 'cases/upload', element: <CasesUpload /> },
        { path: 'cases/pacs', element: <CasesImportFromPacs /> },
        { path: 'cases/run-tasks/:ids/multi-input/:multiInput', element: <CasesRunTasks /> },
        { path: 'cases/viewer/:id', element: <CaseDicomViewer /> },

        { path: 'tasks/list', element: <TaskList /> },

        { path: 'user', element: <UserAccount /> },
        { path: 'users/list', element: <UserList /> },
        { path: 'users/new', element: <UserCreate /> },
        { path: 'users/:name/edit', element: <UserCreate /> },

        { path: 'minomx/list', element: <MinOmxList /> },
        { path: 'minomx/details/:userId', element: <MinOmxDetails /> },

        { path: 'audit-log', element: <AuditLog /> },
      ]
    },
    {
      path: 'system',
      element: (
        <AuthGuard>
          <DashboardWithoutHeaderLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'results/:id', element: <ResultsViewer /> }
      ]
    },


    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'session-inactive', element: <SessionInactive /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <Navigate to="/system" replace />
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD
const CaseList = Loadable(
  lazy(() => import('../pages/dashboard/CaseList'))
);
const CasesExcluded = Loadable(
  lazy(() => import('../pages/dashboard/CasesExcluded'))
);
const CasesArchived = Loadable(
  lazy(() => import('../pages/dashboard/CasesArchived'))
);
const CasesUpload = Loadable(
  lazy(() => import('../pages/dashboard/CasesUpload'))
);
const CasesImportFromPacs = Loadable(
  lazy(() => import('../pages/dashboard/CasesImportFromPacs'))
);
const CasesRunTasks = Loadable(
  lazy(() => import('../pages/dashboard/CasesRunTasks'))
);
const CaseDicomViewer = Loadable(
  lazy(() => import('../pages/dashboard/CaseDicomViewer'))
);
const ResultsViewer = Loadable(
  lazy(() => import('../pages/dashboard/ResultsViewer'))
);
const AuditLog = Loadable(
  lazy(() => import('../pages/dashboard/AuditLog'))
);
const TaskList = Loadable(
  lazy(() => import('../pages/dashboard/TaskList'))
);


// USER
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));


// USER
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));

// MINOMX
const MinOmxList = Loadable(lazy(() => import('../pages/dashboard/MinOmxList')));
const MinOmxDetails = Loadable(lazy(() => import('../pages/dashboard/MinOmxDetails')));

// MAIN
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const SessionInactive = Loadable(lazy(() => import('../pages/SessionInactive')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
